import { Avatar, Box, Chip, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ILiquidityPoolDto from "../../../interfaces/liquidity-pool/ILiquidityPoolDto";
import { LiquidityPoolStatusEnum, LiquidityPoolStatusLabel } from "../../../common/enum/liquidity-pool-status-enum";
import { NetworkLabel } from "../../../common/enum/network-enum";
import { formatPriceToDolar } from "../../../common/utils/number";

interface Props {
    pool: ILiquidityPoolDto;
    index: number;
}

const NetworkColorsDark: Record<number, { bg: string; color: string; border: string }> = {
    1: { bg: 'rgba(98,126,234,0.15)',  color: '#8b9cf7', border: 'rgba(98,126,234,0.4)'  }, // Ethereum
    2: { bg: 'rgba(0,132,255,0.12)',   color: '#4da6ff', border: 'rgba(0,132,255,0.35)'  }, // Base
    3: { bg: 'rgba(40,160,240,0.12)',  color: '#5bbfef', border: 'rgba(40,160,240,0.35)' }, // Arbitrum
    4: { bg: 'rgba(240,185,11,0.12)',  color: '#f0b90b', border: 'rgba(240,185,11,0.35)' }, // BNB Chain
    5: { bg: 'rgba(153,69,255,0.12)',  color: '#bb86fc', border: 'rgba(153,69,255,0.35)' }, // Solana
};

const NetworkColorsLight: Record<number, { bg: string; color: string; border: string }> = {
    1: { bg: 'rgba(61,82,196,0.1)',   color: '#3d52c4', border: 'rgba(61,82,196,0.4)'   }, // Ethereum
    2: { bg: 'rgba(0,85,187,0.08)',   color: '#0055bb', border: 'rgba(0,85,187,0.4)'    }, // Base
    3: { bg: 'rgba(14,114,170,0.08)', color: '#0e72aa', border: 'rgba(14,114,170,0.4)'  }, // Arbitrum
    4: { bg: 'rgba(160,110,0,0.1)',   color: '#7a5500', border: 'rgba(160,110,0,0.4)'   }, // BNB Chain
    5: { bg: 'rgba(100,40,180,0.1)',  color: '#6428b4', border: 'rgba(100,40,180,0.4)'  }, // Solana
};

function statusColor(status: LiquidityPoolStatusEnum): 'success' | 'warning' | 'default' {
    switch (status) {
        case LiquidityPoolStatusEnum.InRange:    return 'success';
        case LiquidityPoolStatusEnum.OutOfRange: return 'warning';
        default:                                 return 'default';
    }
}

function statusBorderColor(status: LiquidityPoolStatusEnum): string {
    switch (status) {
        case LiquidityPoolStatusEnum.InRange:    return 'rgba(76,175,80,0.6)';
        case LiquidityPoolStatusEnum.OutOfRange: return 'rgba(255,152,0,0.6)';
        default:                                 return 'rgba(128,128,128,0.2)';
    }
}

function TokenAvatar({ symbol, imageUrl }: { symbol: string; imageUrl?: string }) {
    return imageUrl
        ? <Avatar src={imageUrl} sx={{ width: 20, height: 20, fontSize: '0.6rem' }} />
        : (
            <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem', bgcolor: 'rgba(255,215,0,0.15)', color: 'primary.main', fontWeight: 700 }}>
                {symbol.slice(0, 2)}
            </Avatar>
        );
}

function RangeBar({ priceLower, priceUpper, currentPrice }: { priceLower: number; priceUpper: number; currentPrice: number }) {
    const theme    = useTheme();
    const inRange  = currentPrice >= priceLower && currentPrice <= priceUpper;
    const rangeSpan = priceUpper - priceLower;

    let markerPercent: number;
    if (rangeSpan <= 0)                   markerPercent = 50;
    else if (currentPrice <= priceLower)  markerPercent = 2;
    else if (currentPrice >= priceUpper)  markerPercent = 98;
    else markerPercent = ((currentPrice - priceLower) / rangeSpan) * 100;

    const color      = inRange ? 'success' : 'warning';
    const trackColor = theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.1)';
    const labelColor = theme.palette.text.secondary;

    return (
        <Box sx={{ minWidth: 210 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
                <Typography variant="body2" fontWeight={600} sx={{ color: labelColor, fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    {formatPriceToDolar(priceLower)}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: labelColor, fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    {formatPriceToDolar(priceUpper)}
                </Typography>
            </Box>
            <Box sx={{ position: 'relative', height: 7, borderRadius: 99, bgcolor: trackColor }}>
                <Box sx={{
                    position: 'absolute', inset: 0, borderRadius: 99,
                    bgcolor: `${color}.main`, opacity: 0.25,
                }} />
                <Tooltip title={`Preço atual: ${formatPriceToDolar(currentPrice)}`} placement="top" arrow>
                    <Box sx={{
                        position: 'absolute',
                        left: `${markerPercent}%`,
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 14, height: 14,
                        borderRadius: '50%',
                        bgcolor: `${color}.main`,
                        border: '2px solid',
                        borderColor: 'background.paper',
                        boxShadow: inRange
                            ? '0 0 7px rgba(76,175,80,0.8)'
                            : '0 0 7px rgba(255,152,0,0.8)',
                        zIndex: 1,
                        cursor: 'default',
                    }} />
                </Tooltip>
            </Box>
        </Box>
    );
}

export default function PoolPositionRow({ pool, index }: Props) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const networkColors = isDark ? NetworkColorsDark : NetworkColorsLight;
    const protocolChip = isDark
        ? { bg: 'rgba(255,215,0,0.08)', color: 'rgba(255,215,0,0.85)', border: 'rgba(255,215,0,0.25)' }
        : { bg: 'rgba(160,130,0,0.1)',  color: '#7a6200',               border: 'rgba(160,130,0,0.35)' };

    const totalFeesPendingUsd = pool.feesPending0Usd + pool.feesPending1Usd;
    const hasRange = pool.priceLower > 0 || pool.priceUpper > 0;

    return (
        <TableRow
            sx={{
                backgroundColor: index % 2 !== 0
                    ? isDark ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.015)'
                    : 'transparent',
                borderLeft: '3px solid',
                borderColor: statusBorderColor(pool.status),
                '&:hover': { backgroundColor: isDark ? 'rgba(255,215,0,0.04)' : 'rgba(201,168,0,0.06)' },
                transition: 'background-color 0.15s ease',
            }}
        >
            {/* Pool */}
            <TableCell align="left" sx={{ py: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TokenAvatar symbol={pool.token0Symbol} imageUrl={pool.token0ImageUrl} />
                        <Box sx={{ ml: '-5px', zIndex: 1 }}>
                            <TokenAvatar symbol={pool.token1Symbol} imageUrl={pool.token1ImageUrl} />
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                            {pool.token0Symbol} / {pool.token1Symbol}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.4 }}>
                            <Chip
                                label={NetworkLabel[pool.network]}
                                size="small"
                                sx={{
                                    fontSize: '0.6rem', height: 16, borderRadius: '4px', px: 0.25,
                                    bgcolor: networkColors[pool.network]?.bg,
                                    color:   networkColors[pool.network]?.color,
                                    border: '1px solid',
                                    borderColor: networkColors[pool.network]?.border,
                                    fontWeight: 600,
                                }}
                            />
                            <Chip
                                label={pool.protocol}
                                size="small"
                                sx={{
                                    fontSize: '0.6rem', height: 16, borderRadius: '4px', px: 0.25,
                                    bgcolor: protocolChip.bg,
                                    color:   protocolChip.color,
                                    border: '1px solid',
                                    borderColor: protocolChip.border,
                                    fontWeight: 600,
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </TableCell>

            {/* Posição Atual */}
            <TableCell align="left" sx={{ py: 1.25 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TokenAvatar symbol={pool.token0Symbol} imageUrl={pool.token0ImageUrl} />
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {pool.token0Amount.toFixed(4)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {pool.token0Symbol}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TokenAvatar symbol={pool.token1Symbol} imageUrl={pool.token1ImageUrl} />
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {pool.token1Amount.toFixed(4)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {pool.token1Symbol}
                        </Typography>
                    </Box>
                </Box>
            </TableCell>

            {/* Balance */}
            <TableCell align="left" sx={{ py: 1.25 }}>
                <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                    {formatPriceToDolar(pool.totalValueUsd)}
                </Typography>
                {pool.walletName && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.2 }}>
                        {pool.walletName}
                    </Typography>
                )}
            </TableCell>

            {/* Taxas Pendentes */}
            <TableCell align="left" sx={{ py: 1.25 }}>
                <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                        fontFamily: 'monospace',
                        color: totalFeesPendingUsd > 0 ? 'primary.main' : 'text.secondary',
                    }}
                >
                    {formatPriceToDolar(totalFeesPendingUsd)}
                </Typography>
                {totalFeesPendingUsd > 0 && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.2 }}>
                        {pool.feesPending0.toFixed(4)} {pool.token0Symbol} · {pool.feesPending1.toFixed(4)} {pool.token1Symbol}
                    </Typography>
                )}
            </TableCell>

            {/* Range */}
            <TableCell align="left" sx={{ py: 1.25 }}>
                {hasRange
                    ? <RangeBar priceLower={pool.priceLower} priceUpper={pool.priceUpper} currentPrice={pool.currentPrice} />
                    : <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>
                }
            </TableCell>

            {/* Preço Atual */}
            <TableCell align="left" sx={{ py: 1.25 }}>
                {pool.currentPrice > 0
                    ? <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatPriceToDolar(pool.currentPrice)}</Typography>
                    : <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>
                }
            </TableCell>

            {/* Estado */}
            <TableCell align="left" sx={{ py: 1.25 }}>
                <Chip
                    label={LiquidityPoolStatusLabel[pool.status]}
                    size="small"
                    color={statusColor(pool.status)}
                    sx={{ fontWeight: 700, fontSize: '0.68rem', borderRadius: '6px', letterSpacing: 0.2 }}
                />
            </TableCell>
        </TableRow>
    );
}
